/**
 * Interface for StillImageBackground background images config
 */
import {IGameModeChangedData, GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import { Sprite, Point, Graphics, Container  } from "pixi.js";
import { TimelineMax, Power2 } from "gsap";
import { EventHandler } from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import { Background } from "./Background";
import { GameEvent } from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import { SpinEvent } from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import { IDimConfig, LayoutDimData, ResizeHelper } from "@nolimitcity/slot-game/bin/core/utils/ResizeHelper";
import { StageManager } from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import { PunkGameConfig } from "../PunkGameConfig";
import { Orientation } from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import { PunkEngineManager } from "../punkEngine/PunkEngineManager";
import { Helper } from "@nolimitcity/slot-game/bin/core/utils/Helper";
import { PunkSymbolData } from "../punkEngine/config/PunkSymbolData";
import { IStillImageBackgroundImagesConfig, IStillImageBackgroundConfig } from "@nolimitcity/slot-game/bin/game/background/StillImageBackground"


export class PunkBackground extends Background {
    protected readonly posterDim:IDimConfig = {
        dimOrientation : {
            landscape :{
                scale:      new Point( 1, 1),
                position:   new Point( -140, 125),
                layout :{
                    left:   new Point( -140, 125),
                    right:   new Point( -140, 125)
                }
            },
            portrait :{
                scale:      new Point( 1, 1),
                position:   new Point( 360, -100)
            }
        },
        WIDTH: 900,
        HEIGHT: 540,
    };

    private static _instance:PunkBackground;
    protected readonly _backgroundImages:IStillImageBackgroundImagesConfig;
    protected _backgroundSprite:Sprite;
    protected _graphOverlay:Graphics;

    protected _logo:Sprite;
    protected _timeline:TimelineMax;

    protected _fogSpine:PIXI.spine.Spine;

    public static get instance():PunkBackground{
        return this._instance;
    }

    constructor(config:IStillImageBackgroundConfig) {
        super(config);
        this._backgroundImages = config.backgroundImages;
        PunkBackground._instance = this;
        this.buttonMode = false;
        this.on('pointertap', () => EventHandler.dispatchEvent(new GameEvent(SpinEvent.SKIP)));
    }

    public changeGraphicAlpha(duration:number, _alpha:number):TimelineMax{
        this._timeline = Helper.timeLineKiller(this._timeline);
        this._timeline = new TimelineMax();
        if(this._graphOverlay.alpha == _alpha) return this._timeline;
        const oldAlpha = this._graphOverlay.alpha;
        this._timeline.fromTo(this._graphOverlay,duration,{alpha:oldAlpha},{alpha:_alpha});
        return this._timeline;
    }

    public setSkipAble(value:boolean):void { this.interactive = value; } 

    public playAttentionBlackFlash(duration:number,_alphaMax:number, _alpha:number):TimelineMax{
        this._timeline = Helper.timeLineKiller(this._timeline);
        this._timeline = new TimelineMax();
        const oldAlpha = this._graphOverlay.alpha;
        this._timeline.fromTo(this._graphOverlay,duration*0.2,{alpha:oldAlpha},{alpha:_alphaMax});
        this._timeline.fromTo(this._graphOverlay,duration*0.8,{alpha:_alphaMax},{alpha:_alpha, ease:Power2.easeOut});
        return this._timeline;
    }

    protected initAnimations():void {
        super.initAnimations();
        this._backgroundSprite = new Sprite(GameResources.getTextures(this._backgroundImages[GameMode.NORMAL].image)[0]);
        this.addChild(this._backgroundSprite);
        this._graphOverlay = new Graphics();
        this.addChild(this._graphOverlay);
        this._graphOverlay.alpha = 0;

        this._logo = new Sprite(GameResources.getTextures("Game_Logo")[0]);
        this._logo.anchor.set(0.5,0.5);
        this.addChild(this._logo);
        this.createGraphic();

        this._fogSpine = new PIXI.spine.Spine(GameResources.getSpineAsset("fog"));
        this._fogSpine.state.setAnimation(0, 'Idle_Fog', true);
        this._fogSpine.alpha = 0.4;
        this._fogSpine.scale.set(2,2);
        StageManager.getLayer(PunkGameConfig.instance.LAYERS.FOG.name).addChild(this._fogSpine);
        // this.addChild(this._fogSpine);
    }

    private createGraphic():void{
        const graphicBg:PIXI.Graphics =  new PIXI.Graphics();
        graphicBg.beginFill(0xFFFFFF, 1);
        graphicBg.drawRect(-PunkSymbolData.DEFAULT_SYMBOL.DIM.WIDTH*0.5, -PunkSymbolData.DEFAULT_SYMBOL.DIM.HEIGHT*0.5,
             PunkEngineManager.ReelAreaWidth, PunkEngineManager.ReelAreaHeight);
        graphicBg.endFill();
        graphicBg.zIndex = -1;
        StageManager.getLayer(PunkGameConfig.instance.LAYERS.SYMBOL_OVERLAY.name).addChild(graphicBg)
        StageManager.getLayer(PunkGameConfig.instance.LAYERS.SYMBOL_OVERLAY.name).mask = graphicBg;
        StageManager.getLayer(PunkGameConfig.instance.LAYERS.SYMBOL_OVERLAY.name).sortChildren();
    }
   
    protected onChangeBackground(data:IGameModeChangedData):boolean {
        if(!super.onChangeBackground(data)) {
            return false;
        }
        return true;
    }

    protected RePaint(posX:number, posY:number, widht:number, height:number):void{
        this._graphOverlay.clear();
        this._graphOverlay.beginFill(0x000000, 1);
        this._graphOverlay.drawRect(posX,posY, widht, height);
        this._graphOverlay.endFill();
    }

    protected onResize():void{
        const layData:LayoutDimData = ResizeHelper.calcDim(this.posterDim);
        this._logo.scale = layData.scale;
        this._logo.position = layData.position;
        this._logo.visible = true;
        if(ResizeHelper.resizeData.orientation === Orientation.PORTRAIT) {
            this._backgroundSprite.pivot = new Point(480, 520);
            this._fogSpine.position = new PIXI.Point(360,330);

            if(ResizeHelper.isIPadd()){
                this._logo.scale.set(0.75, 0.75);
                this._logo.position.set(layData.position.x, - 25 );
            }
        }

        if(ResizeHelper.isIPadd()){
            this._logo.visible = false;
        }
        if(ResizeHelper.IsLandscape()){
            this._fogSpine.position = new PIXI.Point(360,440);
            this._backgroundSprite.pivot = new Point(480, 480);
        }

        this.RePaint(-this._backgroundSprite.pivot.x, -this._backgroundSprite.pivot.y,this._backgroundSprite.texture.width,this._backgroundSprite.texture.height);

        const contain:Container = StageManager.getLayer(PunkGameConfig.instance.LAYERS.SYMBOL_OVERLAY.name);
        
        contain.position = PunkEngineManager.getReelPos(0);
        contain.scale =  PunkEngineManager.dim.scale;
    }
}